// PilotIdentityGate — never-blank identity gate for the /new pilot route.
//
// Production incident (2026-08-13): a cold load / hard reload of /app/new
// mounted the pilot surface before the legacy authenticated-user bootstrap
// (localStorage 'authToken' / 'userInfo' written by src/utils/auth.js and the
// LoginPage flows) had established the tenant identity. The route rendered
// nothing (#root empty) and the live hydration collections fired WITHOUT a
// clientId (400s / 403s). Entering through /app/mypage worked because the
// legacy layout had already completed that bootstrap before redirecting.
//
// This gate reuses the EXACT same bootstrap primitives the legacy routes
// read (utils/auth getUser/getToken + the mappers/clientScope tenant
// resolution) and enforces two invariants for whatever it wraps:
//   1. NEVER-BLANK — while identity is not yet established it renders the
//      caller's `pending` panel (never null), and when identity cannot be
//      established within a bounded time (10s) it renders the caller's
//      `fallback` (App.js passes a redirect to the legacy /mypage surface).
//   2. IDENTITY-BEFORE-HYDRATION — `children` (the pilot surface, including
//      LiveSessionProvider) mount ONLY after the identity snapshot is
//      established, so live collections never fire without a tenant.
//
// Identity is "established" when the stored user has an id AND — for users
// who will actually see the pilot surface (pilot role, non-OEM) — the tenant
// clientId is resolvable. Users the pilot guards redirect away (other roles,
// OEM users) are established as soon as the user record exists: the App.js
// guards own their redirect and must keep running immediately.
//
// While pending, the gate re-reads localStorage on a 250ms poll plus the
// window 'storage' event, so the moment the legacy bootstrap lands the
// surface mounts. Once ready it never polls again (zero overhead on the
// common path, where localStorage already carries the identity at mount).

import { useEffect, useRef, useState } from 'react';
import { getToken, getUser } from '../utils/auth';
import { resolveClientId } from './mappers/clientScope';

export const PILOT_IDENTITY_TIMEOUT_MS = 10_000;
export const PILOT_IDENTITY_POLL_INTERVAL_MS = 250;

// Corrupt localStorage (unparseable userInfo JSON) must degrade to "identity
// not established", never to a render-time throw that blanks #root.
const safeRead = (reader) => {
  if (typeof reader !== 'function') return null;
  try {
    return reader();
  } catch (_error) {
    return null;
  }
};

export const readPilotIdentitySnapshot = ({
  getUserImpl = getUser,
  getTokenImpl = getToken,
  resolveClientIdImpl = resolveClientId,
} = {}) => ({
  user: safeRead(getUserImpl) || null,
  token: safeRead(getTokenImpl) || null,
  clientId: safeRead(resolveClientIdImpl) || null,
});

export const isPilotIdentityEstablished = (snapshot, pilotRoles = []) => {
  const user = snapshot?.user;
  if (!user || user.id === null || user.id === undefined || user.id === '') return false;
  const pilotEligible = pilotRoles.includes(user.role) && !user.oem_id;
  if (!pilotEligible) return true;
  return Boolean(snapshot.clientId);
};

// status: 'ready' | 'pending' | 'unavailable' (bounded wait exhausted).
export const usePilotIdentity = (options = {}) => {
  // Options are captured once: roles/timeouts/impls are static per mount.
  const optionsRef = useRef(options);
  const [identity, setIdentity] = useState(() => {
    const snapshot = readPilotIdentitySnapshot(optionsRef.current);
    const established = isPilotIdentityEstablished(
      snapshot,
      optionsRef.current.pilotRoles || [],
    );
    return { status: established ? 'ready' : 'pending', ...snapshot };
  });

  useEffect(() => {
    if (identity.status !== 'pending') return undefined;
    const {
      pilotRoles = [],
      timeoutMs = PILOT_IDENTITY_TIMEOUT_MS,
      pollIntervalMs = PILOT_IDENTITY_POLL_INTERVAL_MS,
    } = optionsRef.current;
    let active = true;
    const startedAt = Date.now();
    const check = () => {
      if (!active) return;
      const snapshot = readPilotIdentitySnapshot(optionsRef.current);
      if (isPilotIdentityEstablished(snapshot, pilotRoles)) {
        active = false;
        setIdentity({ status: 'ready', ...snapshot });
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        active = false;
        setIdentity({ status: 'unavailable', ...snapshot });
      }
    };
    const intervalId = setInterval(check, pollIntervalMs);
    const eventTarget = typeof window === 'undefined' ? null : window;
    eventTarget?.addEventListener?.('storage', check);
    // The bootstrap may have landed between the initial state read and this
    // effect; re-check immediately instead of waiting a full poll interval.
    check();
    return () => {
      active = false;
      clearInterval(intervalId);
      eventTarget?.removeEventListener?.('storage', check);
    };
  }, [identity.status]);

  return identity;
};

// Default panels keep the gate never-blank even if a caller forgets to pass
// pending/fallback. App.js passes its own loading panel and a router
// redirect to the legacy surface.
const DefaultPendingPanel = () => (
  <div role="status" aria-live="polite">新しい画面を読み込んでいます。</div>
);

const DefaultFallbackPanel = () => (
  <div role="alert">
    <p>ログイン情報を確認できなかったため、新しい画面を表示できません。</p>
    <a href="/app/mypage">旧画面を開く</a>
  </div>
);

export default function PilotIdentityGate({
  pilotRoles = [],
  timeoutMs = PILOT_IDENTITY_TIMEOUT_MS,
  pollIntervalMs = PILOT_IDENTITY_POLL_INTERVAL_MS,
  pending = <DefaultPendingPanel />,
  fallback = <DefaultFallbackPanel />,
  children = null,
  getUserImpl,
  getTokenImpl,
  resolveClientIdImpl,
}) {
  const identity = usePilotIdentity({
    pilotRoles,
    timeoutMs,
    pollIntervalMs,
    getUserImpl,
    getTokenImpl,
    resolveClientIdImpl,
  });
  if (identity.status === 'pending') return pending;
  if (identity.status === 'unavailable') return fallback;
  return typeof children === 'function' ? children(identity) : children;
}
